/**
 * The app's page views for the own visitor counter (SP-D16): the path with `utm_source`, the outside
 * referrer, the window width and the language of the interface, sent as a beacon. No cookie is set for it and
 * nothing is kept in the browser; the server stores no address. Browsers that ask not to be
 * tracked (DNT, GPC) and automated ones (tests) send nothing; neither does the admin area.
 */

const ENDPOINT = '/api/v1/stats/view';

interface PageLocation {
  pathname: string;
  search?: string;
}

/** The part of the data router this needs (react-router's `createBrowserRouter`). */
export interface PageViewRouter {
  state: { location: PageLocation };
  subscribe: (listener: (state: { location: PageLocation }) => void) => () => void;
}

/** The page with `utm_source` of the address, nothing else of the query (a link of a campaign). */
function pageOf({ pathname, search = '' }: PageLocation): string {
  const utm = new URLSearchParams(search).get('utm_source');
  return utm ? `${pathname}?${new URLSearchParams({ utm_source: utm })}` : pathname;
}

type Send = (url: string, body: string) => boolean;

const beacon: Send = (url, body) =>
  navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));

function optedOut(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.webdriver === true || nav.doNotTrack === '1' || nav.globalPrivacyControl === true;
}

const isAdmin = (path: string) => path === '/admin' || path.startsWith('/admin/');

/** Reports the current page and each new one; returns the unsubscribe (nothing when opted out). */
export function countPageViews(
  router: PageViewRouter,
  lang: () => string,
  send: Send = beacon,
): (() => void) | undefined {
  if (optedOut()) return undefined;
  let last: string | null = null;
  const report = (at: PageLocation) => {
    const path = pageOf(at);
    if (path === last) return;
    last = path;
    if (isAdmin(at.pathname)) return;
    const referrer = document.referrer;
    const body = {
      site: 'app',
      path,
      // The app's own pages are no source: the server drops our domains as well.
      ...(referrer && !referrer.startsWith(location.origin) ? { referrer } : {}),
      width: window.innerWidth,
      lang: lang(),
    };
    try {
      send(ENDPOINT, JSON.stringify(body));
    } catch {
      // Counting must never get in the way of the app.
    }
  };
  report(router.state.location);
  return router.subscribe((state) => report(state.location));
}
